import * as React from 'react';
import { SidePanel, WorkspaceButton, WorkspaceBanner } from '@/components/ui/workspace';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';
import type { MapVersion, Validator } from '@/lib/api/process-maps';
import { shortDate } from './map-logic';
import { StatusChip } from '@/components/ui/status-chip';
import { validationChip } from './map-parts';

/**
 * Version history (T-0189): every version, newest first, with what changed
 * and who made it. Any version can be viewed; restoring one is undo, which
 * writes a new version with that content, so numbers only go up (Decision
 * 54.28).
 */

const SOURCE: Record<string, string> = {
  ai_draft: 'Drafted by AI',
  chat: 'Chat',
  manual: 'Step panel',
  canvas: 'Edited by hand',
  revert: 'Undo',
};

export function VersionsPanel({
  versions,
  error,
  latest,
  viewing,
  busy,
  onView,
  onRestore,
  onClose,
}: {
  versions: MapVersion[] | undefined;
  error: unknown;
  latest: number;
  viewing: number;
  busy: boolean;
  onView: (no: number) => void;
  onRestore: (no: number) => void;
  onClose: () => void;
}) {
  const list = [...(versions ?? [])].sort((a, b) => b.no - a.no);
  return (
    <SidePanel aria-label="Versions" path="Versions" onClose={onClose}>
      <p className="text-meta text-ink-500">
        Every change is a new version. Restoring one writes version {latest + 1} with its content;
        nothing is deleted.
      </p>
      {error ? <ErrorMessage error={error} /> : null}
      {!versions && !error ? (
        <div className="flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> Loading versions…
        </div>
      ) : null}
      <ul className="flex flex-col gap-2">
        {list.map((v) => {
          const chip = validationChip(v.validation_state, false);
          return (
            <li
              key={v.no}
              className={cn(
                'rounded-card border px-3 py-2.5',
                v.no === viewing ? 'border-ink-300 bg-paper-50' : 'border-ink-100',
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-body-sm font-semibold text-ink-900">Version {v.no}</span>
                {v.validation_state !== 'none' ? (
                  <StatusChip tone={chip.tone} size="sm">
                    {chip.label}
                  </StatusChip>
                ) : null}
                <span className="flex-1" />
                {v.no === latest ? <span className="text-meta text-ink-500">Latest</span> : null}
              </div>
              <p className="mt-0.5 text-meta text-ink-500">
                {[SOURCE[v.source] ?? v.source, v.created_by?.name, shortDate(v.created_at)]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              <p className="mt-1 text-body-sm text-ink-700">{v.change_summary}</p>
              <div className="mt-2 flex gap-2">
                {v.no !== viewing ? (
                  <WorkspaceButton onClick={() => onView(v.no)}>View</WorkspaceButton>
                ) : null}
                {v.no < latest ? (
                  <WorkspaceButton disabled={busy} onClick={() => onRestore(v.no)}>
                    Restore as version {latest + 1}
                  </WorkspaceButton>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </SidePanel>
  );
}

/**
 * Validate with client (prototype `valBar`): one person per request, from
 * the client's people with an email (Decision 54.33). An inline bar, not a
 * dialog, so nothing covers the map.
 */
export function ValidateBar({
  versionNo,
  validators,
  loading,
  error,
  busy,
  onSend,
  onCancel,
}: {
  versionNo: number;
  validators: Validator[] | undefined;
  loading: boolean;
  error: unknown;
  busy: boolean;
  onSend: (personId: string) => void;
  onCancel: () => void;
}) {
  const [to, setTo] = React.useState('');
  const options = validators ?? [];
  const chosen = to || options[0]?.id || '';
  return (
    <div className="px-4 pt-2">
      <WorkspaceBanner
        actions={
          <>
            <WorkspaceButton variant="tertiary" onClick={onCancel}>
              Cancel
            </WorkspaceButton>
            <WorkspaceButton
              variant="primary"
              disabled={busy || !chosen}
              onClick={() => onSend(chosen)}
            >
              Send secure link
            </WorkspaceButton>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2.5">
          <strong className="font-semibold text-ink-900">Validate version {versionNo} with</strong>
          {loading ? <Spinner /> : null}
          {options.length > 0 ? (
            <select
              aria-label="Who validates"
              value={chosen}
              onChange={(e) => setTo(e.target.value)}
              className="h-8 rounded-card border border-ink-200 bg-paper-0 px-2 text-body-sm text-ink-900"
            >
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title ? `${p.name}, ${p.title}` : p.name}
                </option>
              ))}
            </select>
          ) : !loading ? (
            <span>
              Nobody among the client&rsquo;s people has an email address yet. Add one in Sessions,
              then ask again.
            </span>
          ) : null}
          <span className="text-meta text-ink-500">
            One person per request. They get a secure link to a read-only view of this version,
            where they can comment on any step.
          </span>
          {error ? <ErrorMessage error={error} /> : null}
        </div>
      </WorkspaceBanner>
    </div>
  );
}
